import type { MeUser, Message, PollSummary } from '@/types/api';
import type { DataState } from './dataStore';

/**
 * Pure message-cache reducers, extracted from the store. Each takes the
 * current state and returns a spread-compatible partial (or the same state
 * reference for a no-op). They only touch `messagesByChannel`.
 */

/**
 * Adopt a freshly-fetched history snapshot WITHOUT dropping what arrived
 * while the request was in flight.
 *
 * `setMessages` replaces the array outright, so any `message:create` that
 * landed during the REST round-trip (and any still-pending optimistic
 * bubble) was silently thrown away: open a channel while someone is typing
 * and their message never appeared until you re-entered the channel.
 *
 * Kept from the cache: optimistic `tmp-*` rows, and rows NEWER than the
 * newest fetched message (exactly the in-flight window). Anything older is
 * left to the snapshot, so a server-side delete isn't resurrected and
 * pagination isn't duplicated. Result stays newest-first.
 */
export function mergeMessagesReducer(
  s: DataState,
  channelId: string,
  fetched: Message[],
): Partial<DataState> {
  const cur = s.messagesByChannel[channelId] ?? [];
  if (cur.length === 0) {
    return { messagesByChannel: { ...s.messagesByChannel, [channelId]: fetched } };
  }
  const fetchedIds = new Set(fetched.map((m) => m.id));
  // The snapshot is newest-first, so [0] is the newest row it knows about.
  const newestFetchedAt = fetched.length > 0 ? Date.parse(fetched[0]!.createdAt) : 0;
  const keep = cur.filter((m) => {
    if (fetchedIds.has(m.id)) return false;
    if (m.id.startsWith('tmp-')) return true;
    return Date.parse(m.createdAt) > newestFetchedAt;
  });
  if (keep.length === 0) {
    return { messagesByChannel: { ...s.messagesByChannel, [channelId]: fetched } };
  }
  const merged = [...keep, ...fetched].sort(
    (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
  );
  return { messagesByChannel: { ...s.messagesByChannel, [channelId]: merged } };
}

export function prependMessagesReducer(
  s: DataState,
  channelId: string,
  msgs: Message[],
): Partial<DataState> {
  const cur = s.messagesByChannel[channelId] ?? [];
  const seen = new Set(cur.map((m) => m.id));
  const merged = [...cur];
  for (const m of msgs) {
    if (!seen.has(m.id)) merged.push(m);
  }
  return { messagesByChannel: { ...s.messagesByChannel, [channelId]: merged } };
}

export function upsertMessageReducer(s: DataState, msg: Message): Partial<DataState> | DataState {
  // Thread comments live in the discussion panel, not the main timeline —
  // ignore them here so a comment edit (message:update) doesn't leak a
  // comment into the channel's message list.
  if (msg.threadRootId) return s;
  const cur = s.messagesByChannel[msg.channelId] ?? [];
  const idx = cur.findIndex((m) => m.id === msg.id);
  let next: Message[];
  if (idx >= 0) {
    next = [...cur];
    next[idx] = msg;
  } else {
    next = [msg, ...cur];
  }
  return {
    messagesByChannel: { ...s.messagesByChannel, [msg.channelId]: next },
  };
}

/**
 * A `message:update` from the socket: the server broadcasts it to the whole
 * channel room, so it carries no per-viewer state — a poll's `myOptionIds` is
 * always empty there (it used to be the *actor's* selection, which leaked
 * anonymous votes). Keep the receiver's own selection, as `poll:changed` does.
 * Our own HTTP responses go through `upsertMessageReducer`, which takes the
 * server's value as is.
 */
export function applyRemoteMessageUpdateReducer(
  s: DataState,
  msg: Message,
): Partial<DataState> | DataState {
  if (msg.poll) {
    const existing = s.messagesByChannel[msg.channelId]?.find((m) => m.id === msg.id);
    const mine = existing?.poll?.id === msg.poll.id ? existing.poll.myOptionIds : undefined;
    if (mine) return upsertMessageReducer(s, { ...msg, poll: { ...msg.poll, myOptionIds: mine } });
  }
  return upsertMessageReducer(s, msg);
}

export function applyPollUpdateReducer(
  s: DataState,
  messageId: string,
  poll: PollSummary,
): Partial<DataState> | DataState {
  // The poll:changed broadcast carries only the messageId, so locate the
  // channel that holds it. Keep our own myOptionIds — the server strips
  // the voter's selection from the fan-out to avoid leaking it.
  for (const [channelId, list] of Object.entries(s.messagesByChannel)) {
    const idx = list.findIndex((m) => m.id === messageId);
    const existing = idx >= 0 ? list[idx] : undefined;
    if (!existing) continue;
    const merged: Message = {
      ...existing,
      poll: { ...poll, myOptionIds: existing.poll?.myOptionIds ?? poll.myOptionIds },
    };
    const next = [...list];
    next[idx] = merged;
    return { messagesByChannel: { ...s.messagesByChannel, [channelId]: next } };
  }
  return s;
}

export function removeMessageReducer(
  s: DataState,
  channelId: string,
  id: string,
): Partial<DataState> | DataState {
  const cur = s.messagesByChannel[channelId];
  if (!cur) return s;
  return {
    messagesByChannel: {
      ...s.messagesByChannel,
      [channelId]: cur.filter((m) => m.id !== id),
    },
  };
}

export function replaceMessageReducer(
  s: DataState,
  channelId: string,
  oldId: string,
  nextMsg: Message,
): Partial<DataState> | DataState {
  const cur = s.messagesByChannel[channelId];
  if (!cur) return s;
  const idx = cur.findIndex((m) => m.id === oldId);
  // Atomic swap in ONE pass so subscribers see exactly one render.
  // The confirmed message takes the optimistic bubble's place: with
  // several queued sends (t1, t2, t3) confirming one by one, bubbles keep
  // their order instead of the confirmed one jumping to the top. Any other
  // copy of the real id (the socket echo beat our completion) is dropped.
  if (idx >= 0) {
    const merged: Message[] = [];
    cur.forEach((m, i) => {
      if (i === idx) merged.push(nextMsg);
      else if (m.id !== nextMsg.id) merged.push(m);
    });
    return { messagesByChannel: { ...s.messagesByChannel, [channelId]: merged } };
  }
  // The optimistic entry is gone (e.g. the echo already replaced it):
  // upsert semantics — drop any copy, then prepend (newest-first, as
  // upsertMessage keeps the list).
  const filtered = cur.filter((m) => m.id !== nextMsg.id);
  const merged = [nextMsg, ...filtered];
  return {
    messagesByChannel: { ...s.messagesByChannel, [channelId]: merged },
  };
}

export function applyReactionReducer(
  s: DataState,
  channelId: string,
  messageId: string,
  userId: string,
  emoji: string,
  delta: 1 | -1,
): Partial<DataState> | DataState {
  const cur = s.messagesByChannel[channelId];
  if (!cur) return s;
  const next = cur.map((m) => {
    if (m.id !== messageId) return m;
    const groups = [...m.reactions];
    const i = groups.findIndex((g) => g.emoji === emoji);
    if (delta === 1) {
      if (i >= 0) {
        const g = groups[i]!;
        if (g.users.includes(userId)) return m;
        groups[i] = { ...g, count: g.count + 1, users: [...g.users, userId] };
      } else {
        groups.push({ emoji, count: 1, users: [userId] });
      }
    } else {
      if (i < 0) return m;
      const g = groups[i]!;
      const users = g.users.filter((u) => u !== userId);
      if (users.length === 0) groups.splice(i, 1);
      else groups[i] = { ...g, count: users.length, users };
    }
    return { ...m, reactions: groups };
  });
  return { messagesByChannel: { ...s.messagesByChannel, [channelId]: next } };
}

/** What {@link updateCachedUserReducer} reads: a peer's public profile or my full user. */
export type CachedAuthor = Pick<MeUser, 'id' | 'username' | 'displayName' | 'avatarUrl'>;

export function updateCachedUserReducer(s: DataState, user: CachedAuthor): Partial<DataState> | DataState {
  let changed = false;
  const nextMessages: Record<string, Message[]> = {};
  for (const [channelId, messages] of Object.entries(s.messagesByChannel)) {
    // Skip channels we haven't loaded yet — no messages to patch.
    // Saves O(N×M) walking through 50+ channels on every avatar
    // / status broadcast.
    if (messages.length === 0) {
      nextMessages[channelId] = messages;
      continue;
    }
    let channelChanged = false;
    const nextChannelMessages = messages.map((message) => {
      let nextMessage = message;
      if (message.author.id === user.id) {
        nextMessage = {
          ...nextMessage,
          author: {
            ...message.author,
            username: user.username,
            displayName: user.displayName,
            avatarUrl: user.avatarUrl,
          },
        };
        channelChanged = true;
      }
      if (message.replyTo?.author.id === user.id) {
        nextMessage = {
          ...nextMessage,
          replyTo: {
            ...message.replyTo,
            author: {
              ...message.replyTo.author,
              username: user.username,
              displayName: user.displayName,
            },
          },
        };
        channelChanged = true;
      }
      return nextMessage;
    });
    nextMessages[channelId] = channelChanged ? nextChannelMessages : messages;
    changed ||= channelChanged;
  }
  return changed ? { messagesByChannel: nextMessages } : s;
}
